import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  Button,
  Skeleton,
  MenuItem,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import Header from "../../component/users/Header";
import Footer from "../../component/users/Footer";
import axios from "axios";
import { formatCompetitionDateTime } from "../../utils/competitionDateTime";
import { toast, ToastContainer } from "react-toastify";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import CompetitionApprovedVideos, {
  filterCompetitionVideos,
} from "../../component/users/competitions/CompetitionApprovedVideos";
import CompetitionBuyVotes from "../../component/users/competitions/CompetitionBuyVotes";
import CompetitionBuyVotesTrigger from "../../component/users/competitions/CompetitionBuyVotesTrigger";
import GuestUserRistModal from "../../component/Modals/user/guestUserModalRist/Modal";
import { getUser } from "../../action/authActions";
import { confirmVotePurchaseSession } from "../../api/votePurchase";
import {
  formatVotePurchaseSuccessMessage,
  shouldShowBuyVotesAnywhere,
  resolveMaxFreeVotesPerDay,
  resolveVotePurchaseSessionId,
} from "../../utils/competitionVotePurchase";
import {
  isMissGenerationCompetition,
  MISS_GENERATION_DETAIL_SECTION_BG,
  MISS_GENERATION_DETAIL_SURFACE,
} from "../../utils/missGenerationCompetition";
import MissGenerationBackgroundOverlay from "../../component/users/competitions/MissGenerationBackgroundOverlay";
import CompetitionPromoVideo from "../../component/users/competitions/CompetitionPromoVideo";
import { isCompetitionRegistrationOpen } from "../../utils/competitionRegistration";

const statusStyles = {
  scheduled: { bg: "#455a64", label: "Scheduled" },
  running: { bg: "#2e7d32", label: "Active" },
  paused: { bg: "#ed6c02", label: "Paused" },
  stopped: { bg: "#9e9e9e", label: "Stopped" },
};

const filterSxDark = {
  minWidth: 140,
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    bgcolor: "rgba(255,255,255,0.05)",
    borderRadius: 2,
    "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
  },
  "& .MuiSvgIcon-root": { color: "#fff" },
};

const filterSxLight = {
  minWidth: 140,
  "& .MuiOutlinedInput-root": {
    color: "#222222",
    bgcolor: "rgba(255,255,255,0.55)",
    borderRadius: 2,
    "& fieldset": { borderColor: "#dddddd" },
    "&:hover fieldset": { borderColor: "#cccccc" },
  },
  "& .MuiSvgIcon-root": { color: "#666666" },
};

export default function CompetitionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const authToken = useSelector((state) => state.auth.token);
  const token =
    authToken || localStorage.getItem("mynetgoals.com-token");
  const loggedUser = useSelector((state) => state.loggedUser.user);
  const buyVotesRequestedRef = useRef(searchParams.get("buyVotes") === "1");
  const purchaseHandledRef = useRef(false);
  const [guestModal, setGuestModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);
  const [contestant, setContestant] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [videos, setVideos] = useState([]);
  const [activeEventId, setActiveEventId] = useState(null);
  const [buyVotesOpen, setBuyVotesOpen] = useState(false);
  const [remainingVotes, setRemainingVotes] = useState(0);
  const [dailyVotesUsed, setDailyVotesUsed] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const applyWalletVotes = (count) => {
      const next = Number(count);
      if (!Number.isFinite(next)) return;
      setRemainingVotes(next);
      dispatch({ type: "SET_VOTE_COUNT", payload: next });
    };

    const load = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);

      const params = new URLSearchParams(window.location.search);
      const purchaseResult = params.get("votePurchase");
      const sessionId = resolveVotePurchaseSessionId(params);
      let purchasedRemaining = null;

      if (purchaseResult && !purchaseHandledRef.current) {
        purchaseHandledRef.current = true;
        if (purchaseResult === "success" && sessionId) {
          try {
            const res = await confirmVotePurchaseSession(sessionId, token);
            if (!cancelled && res.success) {
              toast.success(
                formatVotePurchaseSuccessMessage(res, competition?.name)
              );
              if (res.data?.remainingVotes != null) {
                purchasedRemaining = Number(res.data.remainingVotes);
                applyWalletVotes(purchasedRemaining);
              }
              let userId;
              try {
                userId = jwtDecode(token)?.id;
              } catch {
                userId = null;
              }
              if (userId) {
                await dispatch(getUser(token, userId));
                if (purchasedRemaining != null) {
                  applyWalletVotes(purchasedRemaining);
                }
              }
            } else if (!cancelled && res.data?.retryUrl) {
              toast.info(
                res.message ||
                  "That payment method was declined. Choose another way to pay."
              );
              window.location.assign(res.data.retryUrl);
              return;
            } else if (!cancelled) {
              toast.error(res.message || "Could not confirm purchase");
            }
          } catch (e) {
            if (!cancelled) {
              const retryUrl = e?.response?.data?.data?.retryUrl;
              if (retryUrl) {
                toast.info(
                  e.response.data.message ||
                    "That payment method was declined. Choose another way to pay."
                );
                window.location.assign(retryUrl);
                return;
              }
              const raw = e?.response?.data?.message || "";
              toast.error(
                /instrument presented|declined by the processor/i.test(raw)
                  ? "That payment method was declined. On sandbox, log in with a PayPal personal test account (or a PayPal test card like 4111…), not a Stripe 4242 card, then try again."
                  : raw || "Could not confirm your purchase"
              );
            }
          }
        } else if (purchaseResult === "cancelled") {
          toast.info("Vote purchase cancelled");
        }

        params.delete("votePurchase");
        params.delete("session_id");
        params.delete("token");
        params.delete("PayerID");
        params.delete("orderId");
        const qs = params.toString();
        navigate(
          { pathname: `/competitions/${id}`, search: qs ? `?${qs}` : "" },
          { replace: true }
        );
      }

      try {
        const [detailRes, mineRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL_USER}/getCompetitionDetail/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL_USER}/getMyContestant/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);
        if (cancelled) return;
        if (detailRes.data.success) {
          const data = detailRes.data.data;
          setCompetition(data);
          const detailVotes = Number(data?.remainingVotes);
          const nextVotes =
            purchasedRemaining != null
              ? Math.max(
                  purchasedRemaining,
                  Number.isFinite(detailVotes) ? detailVotes : 0
                )
              : data?.remainingVotes ?? 0;
          setRemainingVotes(nextVotes);
          if (purchasedRemaining != null) {
            applyWalletVotes(nextVotes);
          }
          setDailyVotesUsed(
            data?.dailyVotesUsedToday ??
              data?.dailyVotesUsed ??
              0
          );
          const approved = (data?.approved_videos || []).map((v) => ({
            ...v,
            voteCount: v.voteCount ?? v.vote_count ?? 0,
          }));
          setVideos(approved);
          const events = data?.events || [];
          if (events.length > 0) {
            setActiveEventId(events[0].id);
          } else {
            setActiveEventId(null);
          }
        } else {
          toast.error(detailRes.data.message);
        }
        const raw = mineRes.data?.data;
        const mine = Array.isArray(raw) ? raw[0] || null : raw || null;
        setContestant(mine);
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.message || "Failed to load competition");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, token, dispatch, navigate]);

  useEffect(() => {
    if (loading || !competition) return;
    if (window.location.hash === "#competition-videos") {
      const el = document.getElementById("competition-videos");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, competition]);

  useEffect(() => {
    if (searchParams.get("buyVotes") === "1") {
      buyVotesRequestedRef.current = true;
    }
  }, [searchParams]);

  useEffect(() => {
    if (loading || !competition || !buyVotesRequestedRef.current) return;

    buyVotesRequestedRef.current = false;

    if (loggedUser?.device_id) {
      setGuestModal(true);
    } else {
      setBuyVotesOpen(true);
    }

    const params = new URLSearchParams(searchParams);
    params.delete("buyVotes");
    const qs = params.toString();
    navigate(
      { pathname: `/competitions/${id}`, search: qs ? `?${qs}` : "" },
      { replace: true }
    );
  }, [loading, competition, id, navigate, loggedUser, searchParams]);

  const st = statusStyles[competition?.status] || statusStyles.scheduled;
  const isActive = competition?.status === "running";
  const votingEnabled = isActive;
  const isRegistered = Boolean(contestant?.id && contestant?.full_name);
  const registrationOpen = isCompetitionRegistrationOpen(competition);
  const canEnter = isActive && registrationOpen;

  const goEnter = () => {
    if (!registrationOpen) {
      toast.info("Registration and uploads are closed for this competition.");
      return;
    }
    if (loggedUser?.device_id) {
      setGuestModal(true);
      return;
    }
    navigate(`/competitions/${id}/enter`);
  };

  const votePurchase = competition?.vote_purchase;
  const maxFreeVotesPerDay = resolveMaxFreeVotesPerDay(competition);
  const showBuyVotesPrompt = shouldShowBuyVotesAnywhere({
    votePurchase,
    remainingVotes,
    maxFreeVotesPerDay,
    dailyVotesUsed,
  });

  const handleVoteStateChange = (meta) => {
    if (meta?.remainingVotes != null) {
      const next = Number(meta.remainingVotes);
      setRemainingVotes(Number.isFinite(next) ? next : meta.remainingVotes);
      if (Number.isFinite(next)) {
        dispatch({ type: "SET_VOTE_COUNT", payload: next });
      }
    }
    if (meta?.dailyVotesUsedToday != null) {
      setDailyVotesUsed(meta.dailyVotesUsedToday);
    } else if (meta?.requiresPurchase) {
      setDailyVotesUsed(maxFreeVotesPerDay);
      setRemainingVotes(0);
    }
  };

  const openBuyVotes = () => {
    if (loggedUser?.device_id) {
      setGuestModal(true);
      return;
    }
    setBuyVotesOpen(true);
  };

  const competitionEvents = competition?.events || [];

  const filteredVideoCount = useMemo(
    () =>
      filterCompetitionVideos(videos, activeEventId, categoryFilter).length,
    [videos, activeEventId, categoryFilter]
  );

  const activeEventTabIndex = useMemo(() => {
    if (!competitionEvents.length || activeEventId == null) return 0;
    const idx = competitionEvents.findIndex(
      (e) => String(e.id) === String(activeEventId)
    );
    return idx >= 0 ? idx : 0;
  }, [competitionEvents, activeEventId]);

  const missGenerationBranding = useMemo(
    () => isMissGenerationCompetition(id),
    [id]
  );

  return (
    <>
      <ToastContainer />
      <GuestUserRistModal open={guestModal} setOpen={setGuestModal} />
      <CompetitionBuyVotes
        open={buyVotesOpen}
        onClose={() => setBuyVotesOpen(false)}
        competitionId={id}
        competitionName={competition?.name}
        votePurchase={votePurchase}
        token={token}
      />
      <Header />
      <Box className="userLayout">
        <Container
          disableGutters
          maxWidth="lg"
          sx={{
            background: "#242743",
            minHeight: "85vh",
            pb: 4,
            width: "100%",
            overflowX: "hidden",
          }}
        >
          {loading ? (
            <Box sx={{ px: { xs: 2, md: 3 }, pt: 3 }}>
              <Skeleton
                variant="rectangular"
                height={220}
                sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.06)" }}
              />
            </Box>
          ) : !competition ? (
            <Box sx={{ px: 3, pt: 6 }}>
              <Typography color="#fff">Competition not found</Typography>
            </Box>
          ) : (
            <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
              {/* Form-style top bar (matches Add Goal header rhythm) */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                sx={{
                  mb: 2.5,
                  pb: 2,
                  borderBottom: "1px solid #414466",
                }}
              >
                <Button
                  onClick={() => navigate("/competitions")}
                  sx={{
                    color: "#fff",
                    textTransform: "none",
                    alignItems: "center",
                    gap: "6px",
                    minWidth: 0,
                    px: 0,
                  }}
                >
                  <KeyboardBackspaceIcon
                    sx={{ fontSize: { xs: 22, md: 28 } }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 15, md: 18 },
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    Back
                  </Typography>
                </Button>
                <Stack direction="row" gap={1} flexShrink={0} flexWrap="wrap" justifyContent="flex-end">
                  {showBuyVotesPrompt ? (
                    <CompetitionBuyVotesTrigger
                      variant="toolbar"
                      onClick={openBuyVotes}
                      enabled={votePurchase?.enabled}
                    />
                  ) : null}
                  {competition?.final_night_status === "live" ||
                  competition?.final_night_status === "ended" ||
                  competition?.status === "stopped" ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AutoAwesomeOutlinedIcon />}
                      onClick={() =>
                        navigate(`/competitions/${id}/final-night`)
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor:
                          competition?.final_night_status === "live"
                            ? "#d93349"
                            : "#C9A66B",
                        color:
                          competition?.final_night_status === "live"
                            ? "#fff"
                            : "#1a1d27",
                        borderRadius: "8px",
                        px: { xs: 1.2, sm: 2 },
                        "&:hover": {
                          bgcolor:
                            competition?.final_night_status === "live"
                              ? "#b82a3d"
                              : "#b8925a",
                        },
                      }}
                    >
                      {competition?.final_night_status === "live"
                        ? "Final Night Live"
                        : "Final Night"}
                    </Button>
                  ) : null}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<HowToRegIcon />}
                    disabled={!canEnter}
                    onClick={goEnter}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: canEnter ? "#FFCD1F" : "#343759",
                      color: canEnter ? "#111" : "rgba(255,255,255,0.75)",
                      borderRadius: "8px",
                      px: { xs: 1.2, sm: 2 },
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: canEnter ? "#e6b800" : "#343759",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "#343759",
                        color: "rgba(255,255,255,0.7)",
                      },
                    }}
                  >
                    {!registrationOpen && isActive
                      ? "Registration closed"
                      : isRegistered
                        ? isActive
                          ? "Upload video or image"
                          : "My Registration"
                        : "Register for Competition"}
                  </Button>
                </Stack>
              </Stack>

              <Box
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                  ...(missGenerationBranding
                    ? {
                        border: "1px solid #dddddd",
                        bgcolor: MISS_GENERATION_DETAIL_SURFACE,
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 1.5, sm: 2 },
                        boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
                      }
                    : null),
                }}
              >
                {missGenerationBranding ? (
                  <MissGenerationBackgroundOverlay
                    imageUrl={MISS_GENERATION_DETAIL_SECTION_BG}
                    variant="light"
                  />
                ) : null}
                <Box sx={{ position: "relative", zIndex: 1, py: missGenerationBranding ? 0 : 0.5 }}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                mb={1}
              >
                <Chip
                  size="small"
                  label={st.label}
                  sx={{ bgcolor: st.bg, color: "#fff", fontWeight: 700 }}
                />
                {token ? (
                  <Chip
                    size="small"
                    label={`Your votes: ${remainingVotes}`}
                    sx={{
                      bgcolor: "rgba(255,205,31,0.15)",
                      color: "#FFCD1F",
                      fontWeight: 700,
                      border: "1px solid rgba(255,205,31,0.35)",
                    }}
                  />
                ) : null}
                <Typography
                  sx={{
                    color: missGenerationBranding
                      ? "#666666"
                      : "rgba(255,255,255,0.78)",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <CalendarMonthIcon sx={{ fontSize: 16 }} />
                  {formatCompetitionDateTime(competition.start_date, "DD MMM YYYY")} –{" "}
                  {formatCompetitionDateTime(competition.end_date, "DD MMM YYYY")}
                </Typography>
              </Stack>

              <Typography
                sx={{
                  color: missGenerationBranding ? "#222222" : "#fff",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: 26, md: 34 },
                  letterSpacing: "-0.03em",
                  lineHeight: 1.15,
                  mb: 1.5,
                }}
              >
                {competition.name}
              </Typography>

              {competition.promo_video ? (
                <Box
                  sx={{
                    mb: 2.5,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: missGenerationBranding
                      ? "1px solid #dddddd"
                      : "1px solid rgba(255,255,255,0.08)",
                    bgcolor: "#000",
                  }}
                >
                  <CompetitionPromoVideo
                    src={competition.promo_video}
                    poster={competition.banner_img}
                    sx={{ width: "100%", display: "block", maxHeight: 420 }}
                  />
                </Box>
              ) : null}

              <Typography
                component="div"
                sx={{
                  color: missGenerationBranding
                    ? "#1a1a1a"
                    : "rgba(255,255,255,0.72)",
                  fontWeight: missGenerationBranding ? 600 : 400,
                  fontSize: missGenerationBranding ? 15 : undefined,
                  lineHeight: 1.75,
                  mb: 2,
                  maxWidth: 720,
                  whiteSpace: "pre-line",
                  "& strong, & b": {
                    fontWeight: 800,
                    color: missGenerationBranding ? "#000000" : undefined,
                  },
                }}
              >
                {competition.description}
              </Typography>

              <Stack direction="row" gap={1} flexWrap="wrap" mb={2}>
                {(competition.categories || []).map((c) => {
                  const selected = categoryFilter === c.category;
                  return (
                    <Chip
                      key={c.category}
                      size="small"
                      label={c.category}
                      onClick={() =>
                        setCategoryFilter(selected ? "" : c.category)
                      }
                      sx={{
                        cursor: "pointer",
                        bgcolor: missGenerationBranding
                          ? selected
                            ? "rgba(128, 113, 84, 0.14)"
                            : "#f0f0f0"
                          : selected
                            ? "rgba(255,205,31,0.18)"
                            : "rgba(255,255,255,0.06)",
                        color: missGenerationBranding
                          ? selected
                            ? "#807154"
                            : "#444444"
                          : selected
                            ? "#FFCD1F"
                            : "rgba(255,255,255,0.85)",
                        border: missGenerationBranding
                          ? selected
                            ? "1px solid #807154"
                            : "1px solid #dddddd"
                          : selected
                            ? "1px solid rgba(255,205,31,0.45)"
                            : "1px solid rgba(255,255,255,0.12)",
                        fontWeight: 600,
                      }}
                    />
                  );
                })}
              </Stack>
                </Box>
              </Box>

              {/* Videos on detail (no separate page) */}
              <Box
                id="competition-videos"
                sx={{
                  mt: 1,
                  pt: missGenerationBranding ? 0 : 2.5,
                  borderTop: missGenerationBranding
                    ? "none"
                    : "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Box
                  sx={{
                    ...(missGenerationBranding
                      ? {
                          bgcolor: MISS_GENERATION_DETAIL_SURFACE,
                          border: "1px solid #dddddd",
                          borderRadius: 3,
                          boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
                          px: { xs: 1.5, sm: 2 },
                          pt: 2,
                          pb: 0.5,
                          mb: 2,
                        }
                      : null),
                  }}
                >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "flex-end" }}
                  gap={1.5}
                  mb={2}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: missGenerationBranding ? "#222222" : "#fff",
                        fontWeight: 800,
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: 20,
                      }}
                    >
                      Videos & images
                    </Typography>
                    <Typography
                      sx={{
                        color: missGenerationBranding
                          ? "#666666"
                          : "rgba(255,255,255,0.5)",
                        fontSize: 13,
                        mt: 0.4,
                      }}
                    >
                      {filteredVideoCount
                        ? `${filteredVideoCount} approved`
                        : "Approved contestant videos and images"}
                      {votingEnabled
                        ? " · tap the crown to vote"
                        : " · voting opens when Active"}
                      {showBuyVotesPrompt
                        ? " · buy votes when you’ve used your free votes for today"
                        : ""}
                    </Typography>
                  </Box>
                  <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                    {showBuyVotesPrompt ? (
                      <CompetitionBuyVotesTrigger
                        variant="filter"
                        onClick={openBuyVotes}
                        enabled={votePurchase?.enabled}
                        lightSurface={missGenerationBranding}
                      />
                    ) : null}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EmojiEventsOutlinedIcon sx={{ fontSize: 18 }} />}
                      onClick={() => {
                        const qs = categoryFilter
                          ? `?category=${encodeURIComponent(categoryFilter)}`
                          : "";
                        navigate(`/competitions/${id}/leaderboard${qs}`);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        height: 40,
                        color: missGenerationBranding ? "#222222" : "#fff",
                        borderColor: missGenerationBranding
                          ? "#dddddd"
                          : "rgba(255,255,255,0.14)",
                        bgcolor: missGenerationBranding
                          ? "rgba(255,255,255,0.55)"
                          : "rgba(255,255,255,0.05)",
                        borderRadius: 2,
                        px: 1.75,
                        "&:hover": {
                          borderColor: missGenerationBranding
                            ? "rgba(239,54,78,0.45)"
                            : "#FFCD1F",
                          bgcolor: missGenerationBranding
                            ? "rgba(255,255,255,0.85)"
                            : "rgba(255,205,31,0.06)",
                        },
                      }}
                    >
                      Leaderboard
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<HowToRegIcon sx={{ fontSize: 18 }} />}
                      onClick={() => navigate(`/competitions/${id}/voters`)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        height: 40,
                        color: missGenerationBranding ? "#222222" : "#fff",
                        borderColor: missGenerationBranding
                          ? "#dddddd"
                          : "rgba(255,255,255,0.14)",
                        bgcolor: missGenerationBranding
                          ? "rgba(255,255,255,0.55)"
                          : "rgba(255,255,255,0.05)",
                        borderRadius: 2,
                        px: 1.75,
                        "&:hover": {
                          borderColor: missGenerationBranding
                            ? "rgba(239,54,78,0.45)"
                            : "#FFCD1F",
                          bgcolor: missGenerationBranding
                            ? "rgba(255,255,255,0.85)"
                            : "rgba(255,205,31,0.06)",
                        },
                      }}
                    >
                      Voters
                    </Button>
                    <TextField
                      select
                      size="small"
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      sx={
                        missGenerationBranding ? filterSxLight : filterSxDark
                      }
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="">All categories</MenuItem>
                      {(competition.categories || []).map((c) => (
                        <MenuItem key={c.category} value={c.category}>
                          {c.category}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Stack>

                {competitionEvents.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: missGenerationBranding ? 1 : 2.5,
                      borderBottom: missGenerationBranding
                        ? "1px solid #dddddd"
                        : "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Tabs
                      value={activeEventTabIndex}
                      onChange={(_, idx) => {
                        const event = competitionEvents[idx];
                        if (event) setActiveEventId(event.id);
                      }}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        minHeight: 44,
                        "& .MuiTabs-indicator": {
                          bgcolor: "#FFCD1F",
                          height: 3,
                          borderRadius: "3px 3px 0 0",
                        },
                        "& .MuiTab-root": {
                          color: missGenerationBranding
                            ? "#666666"
                            : "rgba(255,255,255,0.55)",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 14,
                          minHeight: 44,
                          px: { xs: 1.5, sm: 2.5 },
                          "&.Mui-selected": {
                            color: missGenerationBranding ? "#807154" : "#FFCD1F",
                          },
                        },
                        "& .MuiTabs-scrollButtons": {
                          color: missGenerationBranding
                            ? "#666666"
                            : "rgba(255,255,255,0.55)",
                        },
                      }}
                    >
                      {competitionEvents.map((e) => (
                          <Tab key={e.id} label={e.name} />
                        ))}
                    </Tabs>
                  </Box>
                ) : null}
                </Box>

                  <CompetitionApprovedVideos
                  videos={videos}
                  activeEventId={activeEventId}
                  categoryFilter={categoryFilter}
                  onCategoryClick={setCategoryFilter}
                  votingEnabled={votingEnabled}
                  votePurchase={votePurchase}
                  onBuyVotes={openBuyVotes}
                  remainingVotes={remainingVotes}
                  maxFreeVotesPerDay={maxFreeVotesPerDay}
                  dailyVotesUsed={dailyVotesUsed}
                  onVoteStateChange={handleVoteStateChange}
                  onVideosChange={setVideos}
                  lightSurface={missGenerationBranding}
                  competitionBanner={competition.banner_img}
                />
              </Box>
            </Box>
          )}
          <Footer />
        </Container>
      </Box>
    </>
  );
}
